import { useContext } from 'react';
import { graphql } from 'react-relay';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	useUpgradeButtonDetailsQuery as QueryData,
	useUpgradeButtonDetailsQueryVariables as QueryVariables,
} from './__types__/useUpgradeButtonDetailsQuery.ts';
import type { ConfluenceEdition } from './__types__/waitForEditionChangeQuery.ts';

export type UpgradeOfferingName = 'standard' | 'premium';

export const useUpgradeButtonDetails = ({
	cloudId,
	targetEdition,
}: {
	cloudId: string;
	targetEdition: ConfluenceEdition;
}): {
	isBillingAdmin: boolean | null;
	isSiteAdmin: boolean;
	isAvailableToUser: boolean;
	experienceUrl: string | null;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const { isSiteAdmin } = useContext(SPAViewContext);
	const offeringName = targetEdition.toLowerCase() as UpgradeOfferingName;

	const { data, loading, error } = useQueryInterop<QueryData, QueryVariables>(
		graphql`
			query useUpgradeButtonDetailsQuery($cloudId: ID!, $offeringName: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlement {
							id
							transactionAccount {
								... on Node {
									id
									__typename
								}
								isCurrentUserBillingAdmin
							}
							experienceCapabilities {
								changeOfferingV2(offeringName: $offeringName) {
									isAvailableToUser
									experienceUrl
								}
							}
						}
					}
				}
			}
		`,
		{
			interopApolloAggClient: true,
			variables: { cloudId, offeringName },
			skip: !cloudId,
		},
	);

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
	const isBillingAdmin = entitlement?.transactionAccount?.isCurrentUserBillingAdmin ?? null;
	const offering = entitlement?.experienceCapabilities?.changeOfferingV2;
	const isAvailableToUser = offering?.isAvailableToUser ?? false;
	const experienceUrl = offering?.experienceUrl ?? null;

	return {
		isBillingAdmin,
		isSiteAdmin,
		isAvailableToUser,
		experienceUrl,
		loading,
		error,
	};
};
